import { api, errorBanner, loadingBanner } from "../api.js";
import { goBack } from "../router.js";
import {
  daysLeftLabel,
  escapeHtml,
  fmtDate,
  fmtDateTime,
  houseLabel,
  housePill,
  memberUrl,
  outlookLabel,
  outlookVariant,
  propositionLabel,
  propositionUrl,
  resultBadge,
  rollCallUrl,
  truncate,
} from "../format.js";
import { renderPdfSection, mountPdfViewer } from "../components/pdfviewer.js";
import { renderCountPills } from "../components/rollcallcard.js";
import { renderOrientations, flattenGroups, renderVoteTable, bindVoteTable } from "../components/votes.js";

export async function renderProposition({ root, match, isCurrent }) {
  const id = match[1];
  const p = await api.proposition(id);
  if (!isCurrent()) return;
  const label = propositionLabel(p);
  document.title = `${label} · Como Vota`;
  const rollCalls = p.roll_calls || [];
  const uniqueCitations = dedupeCitations(p.citations);
  const primaryText = (p.texts || []).find((t) => t.is_primary) || (p.texts || [])[0];

  root.innerHTML = `
    <article class="detail detail--wide">
      <a class="back-link" href="/proposicoes" data-back>← Voltar</a>
      <header class="page-head">
        <div class="detail__meta">
          <span class="eyebrow-pill">${escapeHtml(houseLabel(p.house))}</span>
          ${p.origin_house && p.origin_house !== p.house ? `<span class="meta-muted">origem: ${houseLabel(p.origin_house)}</span>` : ""}
          ${p.is_tramitando === false ? `<span class="pill pill--neutral">encerrada</span>` : ""}
          ${p.norma_gerada ? `<span class="pill pill--ok">virou norma</span>` : ""}
        </div>
        <h1 class="page-title">${escapeHtml(label)}</h1>
        <p class="page-head__kicker" data-title>${escapeHtml(p.summary_one_line || truncateTitle(p.ementa))}</p>
        <div class="detail__meta">
          ${p.regime ? `<span class="meta-badge">${escapeHtml(p.regime)}</span>` : ""}
          ${p.apreciacao ? `<span class="meta-muted">${escapeHtml(p.apreciacao)}</span>` : ""}
          ${p.counterpart ? `<a class="inline-link" href="${propositionUrl(p.counterpart)}" data-link>${escapeHtml(propositionLabel(p.counterpart))} na ${houseLabel(p.counterpart.house)}</a>` : ""}
          ${p.page_url ? `<a class="inline-link" href="${escapeHtml(p.page_url)}" target="_blank" rel="noopener">Página oficial ↗</a>` : ""}
        </div>
      </header>

      <div class="info-box">
        <div class="info-box__item"><span class="info-box__label">Data que a proposta foi apresentada:</span><span class="info-box__value">${p.presented_at ? fmtDate(p.presented_at) : "—"}</span></div>
        <div class="info-box__item"><span class="info-box__label">Situação:</span><span class="info-box__value">${escapeHtml(p.status_situation || p.status_description || "—")}</span>${p.status_organ || p.status_at ? `<span class="meta-muted">${escapeHtml(p.status_organ || "")}${p.status_organ && p.status_at ? " · " : ""}${p.status_at ? fmtDateTime(p.status_at) : ""}</span>` : ""}</div>
      </div>

      <section class="summary-box">
        <h2 class="summary-box__title">Resumo:</h2>
        <div data-summary>${renderSummary(p)}</div>
        ${renderSubjects(p.subjects)}
      </section>

      ${p.authors?.length ? `<div class="authors-line"><span class="authors-line__label">Autoria:</span>${renderAuthors(p.authors)}</div>` : ""}

      <div class="split">
        <section class="split__col">
          <h2 class="section-title">Linha do Tempo</h2>
          <div data-timeline>${loadingBanner("Carregando tramitação…")}</div>
        </section>
        <section class="split__col split__col--right">
          <h2 class="section-title">Texto Completo</h2>
          ${p.ementa ? `<p class="subheading">Ementa oficial</p><p class="muted">${escapeHtml(p.ementa)}</p>` : ""}
          ${p.ementa_detalhada ? `<p class="subheading">Ementa detalhada</p><p class="muted">${escapeHtml(p.ementa_detalhada)}</p>` : ""}
          ${p.keywords ? `<p class="subheading">Palavras-chave</p><p class="muted">${escapeHtml(p.keywords)}</p>` : ""}
          ${primaryText ? `<p><a class="btn btn--primary" href="${escapeHtml(primaryText.original_url)}" target="_blank" rel="noopener">Abrir Documento Oficial</a></p>` : ""}
          ${p.texts?.length ? `<p><a class="inline-link" href="#texto-integral">Ler o texto integral aqui embaixo ↓</a></p>` : ""}
        </section>
      </div>

      <section class="detail__section" id="texto-integral">
        <h2 class="section-title">Texto integral</h2>
        <div data-pdf-body>${renderPdfSection(p.texts)}</div>
      </section>

      <section class="detail__section">
        <h2 class="section-title">Próximos passos</h2>
        ${renderNextSteps(p.next_steps)}
      </section>

      <section class="detail__section">
        <h2 class="section-title">Leis citadas${uniqueCitations.length ? ` <span class="tabbar__count">${uniqueCitations.length}</span>` : ""}</h2>
        ${renderCitations(uniqueCitations)}
      </section>
    </article>`;

  root.querySelector("[data-back]").addEventListener("click", (e) => {
    e.preventDefault();
    goBack("/proposicoes");
  });

  if (p.texts?.length) mountPdfViewer(root.querySelector("[data-pdf-body]"), p.texts);
  if (!p.enrichment?.summary_layman) requestSummary();
  loadTimeline();

  // --- on-demand summary ---------------------------------------------------------
  async function requestSummary(attempt = 0) {
    const box = root.querySelector("[data-summary]");
    const note = () => box.querySelector("[data-summary-note]");
    try {
      const res = await api.summarize(id);
      if (!isCurrent()) return;
      if (res.status === "ready" && res.enrichment?.summary_layman) {
        p.enrichment = res.enrichment;
        box.innerHTML = renderSummary(p);
        if (!p.summary_one_line && res.enrichment.summary_one_line) {
          p.summary_one_line = res.enrichment.summary_one_line;
          root.querySelector("[data-title]").textContent = p.summary_one_line;
        }
        return;
      }
      if (res.status === "pending" && attempt < 3) {
        setTimeout(() => requestSummary(attempt + 1), 3000 * (attempt + 1));
        return;
      }
    } catch (err) {
      console.warn("summary unavailable", err);
    }
    if (isCurrent() && note()) note().textContent = "Resumo indisponível — exibindo a ementa oficial.";
  }

  // --- merged timeline (tramitações + votações) --------------------------------------
  let order = "desc";
  let events = null;
  async function loadTimeline() {
    const el = root.querySelector("[data-timeline]");
    try {
      const trams = await api.tramitacoesAll(id, {
        onProgress: (n, total) => {
          if (isCurrent() && total > n) el.innerHTML = loadingBanner(`Carregando tramitação (${n} de ${total})…`);
        },
      });
      if (!isCurrent()) return;
      events = [
        ...trams.map((t) => ({ at: t.happened_at, html: renderTramitacao(t) })),
        ...rollCalls.map((r) => ({ at: r.happened_at, html: renderTimelineVote(r, rollCalls.length <= 2) })),
      ].sort((a, b) => new Date(a.at) - new Date(b.at));
      renderTimeline(el);
    } catch (err) {
      if (!isCurrent()) return;
      el.innerHTML = errorBanner(err);
    }
  }

  function renderTimeline(el) {
    if (!events.length) {
      el.innerHTML = `<p class="muted">Sem eventos registrados.</p>`;
      return;
    }
    const list = order === "asc" ? events : [...events].reverse();
    el.innerHTML = `
      <div class="section-head">
        <span class="meta-muted">${events.length - rollCalls.length} tramitações · ${rollCalls.length} ${rollCalls.length === 1 ? "votação" : "votações"}</span>
        <button type="button" class="btn btn--ghost btn--sm" data-order>${order === "asc" ? "Mais recentes primeiro" : "Mais antigas primeiro"}</button>
      </div>
      <ol class="timeline timeline--merged">${list.map((e) => e.html).join("")}</ol>`;
    el.querySelector("[data-order]").addEventListener("click", () => {
      order = order === "asc" ? "desc" : "asc";
      renderTimeline(el);
    });
    el.querySelectorAll("[data-rc]").forEach((details) => {
      let loaded = false;
      const open = () => {
        if (loaded) return;
        loaded = true;
        loadRollCallDetails(details);
      };
      details.addEventListener("toggle", () => details.open && open());
      if (details.open) open();
    });
  }

  async function loadRollCallDetails(details) {
    const body = details.querySelector("[data-rc-body]");
    body.innerHTML = loadingBanner("Carregando votos…");
    try {
      const r = await api.rollCall(details.dataset.rc);
      if (!isCurrent()) return;
      const orientations = (r.orientations || []).filter((o) => o.orientation);
      const govOfficial = orientations.some((o) => o.bancada_kind === "governo" && o.source === "official");
      const rows = flattenGroups(r.groups);
      body.innerHTML = `
        ${orientations.length ? `<h4 class="subheading">Orientação dos partidos</h4>${renderOrientations(orientations, govOfficial)}` : ""}
        ${
          rows.length
            ? `<h4 class="subheading">Votos nominais</h4>${renderVoteTable(rows)}`
            : r.is_secret
              ? `<p class="muted">Votação secreta: os votos individuais não são divulgados.</p>`
              : `<p class="muted">Votação sem registro nominal individual disponível (apenas totais).</p>`
        }`;
      bindVoteTable(body, rows);
    } catch (err) {
      if (!isCurrent()) return;
      body.innerHTML = errorBanner(err);
    }
  }
}

function truncateTitle(text) {
  if (!text) return "Proposição";
  const t = text.trim();
  return t.length > 160 ? t.slice(0, 159) + "…" : t;
}

function renderSummary(p) {
  const e = p.enrichment;
  if (e?.summary_layman) {
    const paras = e.summary_layman
      .split(/\n{2,}|\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => `<p>${escapeHtml(s)}</p>`)
      .join("");
    const note = e.is_lite
      ? `Resumo automático gerado a partir da ementa (${escapeHtml(e.model || "IA")}). Confira o texto integral antes de tirar conclusões.`
      : `Resumo gerado por IA (${escapeHtml(e.model || "modelo")}${e.completed_at ? `, ${fmtDate(e.completed_at)}` : ""}). Confira o texto integral antes de tirar conclusões.`;
    return `<div class="summary">${paras}<p class="meta-muted">${note}</p></div>`;
  }
  return `<div class="summary"><p>${escapeHtml(p.ementa || "Sem ementa disponível.")}</p><p class="meta-muted" data-summary-note aria-live="polite">Gerando resumo… exibindo a ementa oficial enquanto isso.</p></div>`;
}

function renderSubjects(subjects) {
  if (!subjects?.length) return "";
  return `<div class="tags">${subjects
    .map(
      (s) =>
        `<a class="tag tag--${s.source === "llm" ? "llm" : "official"}" href="/proposicoes?subject=${encodeURIComponent(s.slug)}" data-link>${escapeHtml(s.label)}<span class="tag__badge">${s.source === "llm" ? "IA" : "oficial"}</span></a>`,
    )
    .join("")}</div>`;
}

function renderNextSteps(ns) {
  if (!ns) return `<p class="muted">Sem informações suficientes sobre a tramitação.</p>`;
  const steps = (ns.path || [])
    .map(
      (s) => `<li class="tl tl--${escapeHtml(s.status)}">
        <span class="tl__dot"></span>
        <div class="tl__body">
          <strong>${escapeHtml(s.step)}</strong> <span class="meta-muted">${escapeHtml(s.where || "")}</span>
          ${s.note ? `<p class="muted">${escapeHtml(s.note)}</p>` : ""}
        </div>
      </li>`,
    )
    .join("");
  const deadlines = (ns.deadlines || [])
    .map(
      (d) =>
        `<span class="total-pill ${d.days_left !== null && d.days_left !== undefined && d.days_left < 15 ? "total-pill--nao" : ""}"><strong>${escapeHtml(d.label)}</strong> ${fmtDate(d.date)}${d.days_left !== null && d.days_left !== undefined ? ` · ${daysLeftLabel(d.days_left)}` : ""}</span>`,
    )
    .join("");
  const llm = (ns.llm_path || [])
    .map((s) => `<li><strong>${escapeHtml(s.step || "")}</strong> ${s.where ? `<span class="meta-muted">${escapeHtml(s.where)}</span>` : ""}${s.note ? ` — ${escapeHtml(s.note)}` : ""}</li>`)
    .join("");
  return `
    <div class="nextsteps">
      <div class="nextsteps__head">
        <span class="pill pill--warn">${escapeHtml(ns.current_stage || "")}</span>
        ${ns.terminal ? `<span class="pill pill--neutral">tramitação encerrada</span>` : ""}
        <span class="outlook outlook--${outlookVariant(ns.outlook)}">Chance de aprovação: <strong>${escapeHtml(outlookLabel(ns.outlook))}</strong> <span class="meta-muted">(${ns.outlook_source === "llm" ? "análise do texto" : "regras de tramitação"})</span></span>
      </div>
      ${deadlines ? `<div class="totals">${deadlines}</div>` : ""}
      ${steps ? `<ol class="timeline timeline--steps">${steps}</ol>` : ""}
      ${ns.rationale ? `<p class="rationale">${escapeHtml(ns.rationale)}</p>` : ""}
      ${llm ? `<h3 class="subheading">Leitura do texto</h3><ul class="bullets">${llm}</ul>` : ""}
    </div>`;
}

function dedupeCitations(citations) {
  const seen = new Set();
  const out = [];
  for (const c of citations || []) {
    const key = `${c.normalized_key}|${c.article || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

function renderCitations(citations) {
  if (!citations?.length) return `<p class="muted">Nenhuma norma citada foi identificada.</p>`;
  const items = citations.map((c) => {
    const label = c.raw_text || c.normalized_key;
    let link = "";
    if (c.cited_proposition_id) link = `<a class="inline-link" href="/proposicao/${c.cited_proposition_id}" data-link>ver proposição</a>`;
    else if (c.external_url) link = `<a class="inline-link" href="${escapeHtml(c.external_url)}" target="_blank" rel="noopener">consultar ↗</a>`;
    return `<li class="cite"><span class="cite__kind">${escapeHtml(kindLabel(c.kind))}</span> <span>${escapeHtml(label)}</span>${c.article ? ` <span class="meta-muted">${escapeHtml(c.article)}</span>` : ""} ${link} <span class="tag__badge">${c.source === "llm" ? "IA" : "texto"}</span></li>`;
  });
  return `<ul class="cites">${items.join("")}</ul>`;
}

function kindLabel(kind) {
  const map = {
    constituicao: "Constituição",
    emenda_constitucional: "EC",
    lei: "Lei",
    lei_complementar: "LC",
    decreto: "Decreto",
    decreto_lei: "Decreto-Lei",
    medida_provisoria: "MP",
    resolucao: "Resolução",
    codigo: "Código",
    proposicao: "Proposição",
    outro: "Norma",
  };
  return map[kind] || kind;
}

function renderAuthors(authors) {
  if (!authors?.length) return `<p class="muted">Autoria não informada.</p>`;
  return `<ul class="people-list">${authors
    .map((a) => {
      const name = a.member ? `<a href="${memberUrl(a.member)}" data-link>${escapeHtml(a.member.name)}</a>` : escapeHtml(a.name);
      const party = a.member?.party_sigla ? `<span class="people-party">${escapeHtml(a.member.party_sigla)}${a.member.uf ? `/${escapeHtml(a.member.uf)}` : ""}</span>` : "";
      return `<li class="people-item"><span class="people-name">${name}</span>${party}${a.is_first_author ? `<span class="people-tag">1º autor</span>` : a.author_type ? `<span class="people-tag">${escapeHtml(a.author_type)}</span>` : ""}</li>`;
    })
    .join("")}</ul>`;
}

function renderTramitacao(t) {
  return `<li class="tl">
    <span class="tl__dot"></span>
    <div class="tl__body">
      <span class="meta-muted">${fmtDateTime(t.happened_at)}${t.organ_sigla ? ` · ${escapeHtml(t.organ_sigla)}` : ""}${t.house_of_organ ? ` (${escapeHtml(t.house_of_organ)})` : ""}</span>
      <p>${escapeHtml(t.description || t.status_description || "")}</p>
      ${t.dispatch ? `<p class="muted">${escapeHtml(t.dispatch)}</p>` : ""}
      ${t.url ? `<a class="inline-link" href="${escapeHtml(t.url)}" target="_blank" rel="noopener">documento ↗</a>` : ""}
    </div>
  </li>`;
}

/** A roll call inline in the timeline: result tag, counts, and a lazy details block with orientations + nominal votes. */
function renderTimelineVote(r, openByDefault) {
  const showDetails = r.is_nominal !== false && !r.is_secret;
  return `<li class="tl tl--vote">
    <span class="tl__dot tl__dot--vote"></span>
    <div class="tl__body tl-vote">
      <div class="tl-vote__head">
        ${resultBadge(r.result)}
        ${r.is_secret ? `<span class="pill pill--sm pill--neutral">secreta</span>` : ""}
        ${r.is_nominal === false ? `<span class="pill pill--sm pill--neutral">simbólica</span>` : ""}
      </div>
      <div class="tl-vote__head">
        <span class="pill">Votação</span>
        <span class="meta-muted">${fmtDateTime(r.happened_at)}</span>
      </div>
      ${r.description ? `<p class="tl-vote__desc">${escapeHtml(truncate(r.description, 300))}</p>` : ""}
      <div class="totals">${renderCountPills(r)}</div>
      ${
        showDetails
          ? `<details class="rc-details" data-rc="${r.id}" ${openByDefault ? "open" : ""}>
              <summary>Orientações e votos nominais</summary>
              <div data-rc-body></div>
            </details>`
          : r.is_secret
            ? `<p class="meta-muted">Votação secreta: os votos individuais não são divulgados.</p>`
            : `<p class="meta-muted">Votação simbólica, sem registro nominal.</p>`
      }
      <a class="card__link" href="${rollCallUrl(r)}" data-link>Ver página da votação →</a>
    </div>
  </li>`;
}
